import React, { useState, useEffect } from 'react';
import { Modal, Heading, Text, Section } from './ui';
import { getUserAnalytics, toggleAdminStatus, toggleBetaStatus, toggleFamilyStatus, resetWalkthroughStatus, resetLineageWalkthrough, resetLiveSessionWalkthrough } from '../services/api';

const UserDetailModal = ({ isOpen, onClose, user, currentUser, onUserUpdate }) => {
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [isUpdating, setIsUpdating] = useState(false);

    useEffect(() => {
        if (isOpen && user && currentUser) {
            fetchUserAnalytics();
        }
    }, [isOpen, user, currentUser]);

    const fetchUserAnalytics = async () => {
        try {
            setLoading(true);
            setError('');
            
            if (!currentUser) {
                throw new Error('No authenticated user found');
            }
            
            const token = await currentUser.getIdToken();
            const data = await getUserAnalytics(user.id, token);
            setAnalytics(data);
        } catch (err) {
            console.error('Error fetching user analytics:', err);
            setError(err.message || 'Failed to load user analytics');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Never';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatDuration = (minutes) => {
        if (!minutes) return 'N/A';
        const hours = Math.floor(minutes / 60);
        const mins = Math.round(minutes % 60);
        return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
    };

    const handleToggleAdmin = async () => {
        if (!currentUser) return;
        try {
            setIsUpdating(true);
            const token = await currentUser.getIdToken();
            await toggleAdminStatus(user.id, token);
            if (onUserUpdate) onUserUpdate();
            // Reload page to show updated status
            window.location.reload();
        } catch (err) {
            console.error('Error toggling admin status:', err);
            setIsUpdating(false);
        }
    };

    const handleToggleBeta = async () => {
        if (!currentUser) return;
        try {
            setIsUpdating(true);
            const token = await currentUser.getIdToken();
            await toggleBetaStatus(user.id, token);
            if (onUserUpdate) onUserUpdate();
            // Reload page to show updated status
            window.location.reload();
        } catch (err) {
            console.error('Error toggling beta status:', err);
            setIsUpdating(false);
        }
    };

    const handleToggleFamily = async () => {
        if (!currentUser) return;
        try {
            setIsUpdating(true);
            const token = await currentUser.getIdToken();
            await toggleFamilyStatus(user.id, token);
            if (onUserUpdate) onUserUpdate();
            // Reload page to show updated status
            window.location.reload();
        } catch (err) {
            console.error('Error toggling family status:', err);
            setIsUpdating(false);
        }
    };

    const handleResetWalkthrough = async () => {
        if (!currentUser) return;
        try {
            setIsUpdating(true);
            const token = await currentUser.getIdToken();
            await resetWalkthroughStatus(user.id, token);
            if (onUserUpdate) onUserUpdate();
            // Reload page to show updated status
            window.location.reload();
        } catch (err) {
            console.error('Error resetting walkthrough status:', err);
            setIsUpdating(false);
        }
    };

    const handleResetLineageWalkthrough = async () => {
        if (!currentUser) return;
        try {
            setIsUpdating(true);
            const token = await currentUser.getIdToken();
            await resetLineageWalkthrough(user.id, token);
            if (onUserUpdate) onUserUpdate();
            // Reload page to show updated status
            window.location.reload();
        } catch (err) {
            console.error('Error resetting lineage walkthrough:', err);
            setIsUpdating(false);
        }
    };

    const handleResetLiveSessionWalkthrough = async () => {
        if (!currentUser) return;
        try {
            setIsUpdating(true);
            const token = await currentUser.getIdToken();
            await resetLiveSessionWalkthrough(user.id, token);
            if (onUserUpdate) onUserUpdate();
            // Reload page to show updated status
            window.location.reload();
        } catch (err) {
            console.error('Error resetting live session walkthrough:', err);
            setIsUpdating(false);
        }
    };

    const formatSeconds = (seconds) => {
        if (!seconds) return 'N/A';
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
    };

    if (!user) return null;

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={`USER ANALYTICS: ${user.display_name || user.username}`} maxWidth="max-w-6xl">
            <div className="vspace-lg">
                {loading && (
                    <div className="text-center py-8">
                        <Text className="text-neutral-900 opacity-60 font-mono">Loading user analytics...</Text>
                    </div>
                )}

                {error && (
                    <div className="bg-neutral-50 border border-neutral-900 p-4 mb-4">
                        <Text className="text-neutral-900 font-mono">{error}</Text>
                    </div>
                )}

                {analytics && (
                    <>
                        {/* Activity Timeline - Main Table */}
                        <Section>
                            <Heading level={4} className="text-neutral-900 font-mono uppercase tracking-wider mb-4">ACTIVITY TIMELINE</Heading>
                            <div className="bg-neutral-0 hairline overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-neutral-50">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-mono uppercase tracking-wider text-neutral-900">DATE</th>
                                                <th className="px-4 py-3 text-left text-xs font-mono uppercase tracking-wider text-neutral-900">TYPE</th>
                                                <th className="px-4 py-3 text-left text-xs font-mono uppercase tracking-wider text-neutral-900">ACTION</th>
                                                <th className="px-4 py-3 text-left text-xs font-mono uppercase tracking-wider text-neutral-900">ITEM</th>
                                                <th className="px-4 py-3 text-left text-xs font-mono uppercase tracking-wider text-neutral-900">ID</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-neutral-200">
                                            {analytics.analytics.recentActivity && analytics.analytics.recentActivity.length > 0 ? (
                                                analytics.analytics.recentActivity.slice(0, 10).map((activity, index) => (
                                                    <tr key={index} className="hover:bg-neutral-50">
                                                        <td className="px-4 py-3 text-sm font-mono text-neutral-900">
                                                            {formatDate(activity.created_at)}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm font-mono text-neutral-900">
                                                            <span className={`px-2 py-1 text-xs font-mono uppercase tracking-wider ${
                                                                activity.type === 'recipe' 
                                                                    ? 'bg-blue-100 text-blue-800' 
                                                                    : 'bg-green-100 text-green-800'
                                                            }`}>
                                                                {activity.type}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3 text-sm font-mono text-neutral-900">
                                                            {activity.action}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm font-mono text-neutral-900">
                                                            {activity.title}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm font-mono text-neutral-900 opacity-60">
                                                            {activity.id}
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="5" className="px-4 py-8 text-center text-sm font-mono text-neutral-900 opacity-60">
                                                        No recent activity
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </Section>

                        {/* User Info Cards */}
                        <Section>
                            <Heading level={4} className="text-neutral-900 font-mono uppercase tracking-wider mb-4">USER INFORMATION</Heading>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {/* Basic Info Card */}
                                <div className="bg-neutral-0 hairline p-4 space-y-3">
                                    <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider">BASIC INFO</Text>
                                    <div className="space-y-2">
                                        <div>
                                            <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider">USERNAME</Text>
                                            <Text className="text-neutral-900 font-mono font-medium">{user.username}</Text>
                                        </div>
                                        <div>
                                            <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider">EMAIL</Text>
                                            <Text className="text-neutral-900 font-mono font-medium break-all">{user.email}</Text>
                                        </div>
                                        <div>
                                            <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider">JOINED</Text>
                                            <Text className="text-neutral-900 font-mono font-medium">{formatDate(user.created_at)}</Text>
                                        </div>
                                        <div>
                                            <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider">LAST ACTIVE</Text>
                                            <Text className="text-neutral-900 font-mono font-medium">{formatDate(user.updated_at)}</Text>
                                        </div>
                                        <div>
                                            <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider">WALKTHROUGH</Text>
                                            <Text className="text-neutral-900 font-mono font-medium uppercase">
                                                {user.walkthrough_status || 'not_started'}
                                            </Text>
                                            {user.walkthrough_completed_at && (
                                                <Text className="text-neutral-900 opacity-60 text-xs font-mono mt-1">
                                                    Completed: {formatDate(user.walkthrough_completed_at)}
                                                </Text>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Subscription Card */}
                                <div className="bg-neutral-0 hairline p-4 space-y-3">
                                    <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider">SUBSCRIPTION</Text>
                                    <div className="space-y-2">
                                        <div>
                                            <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider">PLAN</Text>
                                            <Text className="text-neutral-900 font-mono font-medium uppercase">{user.plan_type || 'free'}</Text>
                                        </div>
                                        {user.subscription_status && (
                                            <div>
                                                <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider">STATUS</Text>
                                                <Text className="text-neutral-900 font-mono font-medium">{user.subscription_status}</Text>
                                            </div>
                                        )}
                                        <div>
                                            <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider">RECIPE LIMIT</Text>
                                            <Text className="text-neutral-900 font-mono font-medium">
                                                UNLIMITED
                                            </Text>
                                        </div>
                                        {user.engagement_tier && (
                                            <div>
                                                <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider">ENGAGEMENT TIER</Text>
                                                <Text className="text-neutral-900 font-mono font-medium uppercase">
                                                    {user.engagement_tier}
                                                </Text>
                                            </div>
                                        )}
                                        <div>
                                            <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider">PROFILE</Text>
                                            <div className="flex items-center gap-2">
                                                <span className={`inline-block w-3 h-3 rounded-full ${user.is_public ? 'bg-blue-500' : 'bg-neutral-300'}`}></span>
                                                <Text className="text-neutral-900 font-mono font-medium">{user.is_public ? 'PUBLIC' : 'PRIVATE'}</Text>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* User Type Card */}
                                <div className="bg-neutral-0 hairline p-4 space-y-3">
                                    <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider">USER TYPE</Text>
                                    <div className="space-y-2">
                                        <div className="flex items-center gap-2">
                                            {user.is_admin && (
                                                <span className="inline-block w-3 h-3 rounded-full bg-red-500" title="Admin"></span>
                                            )}
                                            {user.is_family && (
                                                <span className="inline-block w-3 h-3 rounded-full bg-blue-500" title="Family"></span>
                                            )}
                                            {user.is_beta && (
                                                <span className="inline-block w-3 h-3 rounded-full bg-purple-500" title="Beta"></span>
                                            )}
                                            {!user.is_admin && !user.is_family && !user.is_beta && (
                                                <span className="inline-block w-3 h-3 rounded-full bg-neutral-300" title="User"></span>
                                            )}
                                            <Text className="text-neutral-900 font-mono font-medium">
                                                {user.is_admin ? 'ADMIN' : user.is_family ? 'FAMILY' : user.is_beta ? 'BETA' : 'USER'}
                                            </Text>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Section>

                        {/* User Management */}
                        <Section>
                            <Heading level={4} className="text-neutral-900 font-mono uppercase tracking-wider mb-4">USER MANAGEMENT</Heading>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                <button
                                    onClick={handleToggleAdmin}
                                    disabled={isUpdating}
                                    className={`px-4 py-3 font-mono text-sm uppercase tracking-wider transition-colors ${
                                        user.is_admin 
                                            ? 'bg-red-600 text-white hover:bg-red-700' 
                                            : 'bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0'
                                    }`}
                                >
                                    {isUpdating ? 'UPDATING...' : user.is_admin ? 'REMOVE ADMIN' : 'MAKE ADMIN'}
                                </button>
                                <button
                                    onClick={handleToggleFamily}
                                    disabled={isUpdating}
                                    className={`px-4 py-3 font-mono text-sm uppercase tracking-wider transition-colors ${
                                        user.is_family 
                                            ? 'bg-blue-600 text-white hover:bg-blue-700' 
                                            : 'bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0'
                                    }`}
                                >
                                    {isUpdating ? 'UPDATING...' : user.is_family ? 'REMOVE FAMILY' : 'MAKE FAMILY'}
                                </button>
                                <button
                                    onClick={handleToggleBeta}
                                    disabled={isUpdating}
                                    className={`px-4 py-3 font-mono text-sm uppercase tracking-wider transition-colors ${
                                        user.is_beta 
                                            ? 'bg-purple-600 text-white hover:bg-purple-700' 
                                            : 'bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0'
                                    }`}
                                >
                                    {isUpdating ? 'UPDATING...' : user.is_beta ? 'REMOVE BETA' : 'MAKE BETA'}
                                </button>
                                <button
                                    onClick={handleResetWalkthrough}
                                    disabled={isUpdating}
                                    className="px-4 py-3 font-mono text-sm uppercase tracking-wider transition-colors bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0"
                                >
                                    {isUpdating ? 'RESETTING...' : 'RESET INITIAL WALKTHROUGH'}
                                </button>
                            </div>
                        </Section>

                        {/* Walkthrough Management */}
                        <Section>
                            <Heading level={4} className="text-neutral-900 font-mono uppercase tracking-wider mb-4">WALKTHROUGH MANAGEMENT</Heading>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Initial Walkthrough */}
                                <div className="bg-neutral-0 hairline p-4 space-y-3">
                                    <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider">INITIAL WALKTHROUGH</Text>
                                    <div className="space-y-2">
                                        <div>
                                            <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider">STATUS</Text>
                                            <Text className="text-neutral-900 font-mono font-medium uppercase">
                                                {user.walkthrough_status || 'not_started'}
                                            </Text>
                                        </div>
                                        {user.walkthrough_completed_at && (
                                            <div>
                                                <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider">COMPLETED</Text>
                                                <Text className="text-neutral-900 font-mono font-medium text-xs">
                                                    {formatDate(user.walkthrough_completed_at)}
                                                </Text>
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        onClick={handleResetWalkthrough}
                                        disabled={isUpdating}
                                        className="w-full px-4 py-2 font-mono text-sm uppercase tracking-wider transition-colors bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0"
                                    >
                                        {isUpdating ? 'RESETTING...' : 'RESET'}
                                    </button>
                                </div>

                                {/* Lineage Walkthrough */}
                                <div className="bg-neutral-0 hairline p-4 space-y-3">
                                    <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider">LINEAGE WALKTHROUGH</Text>
                                    <div className="space-y-2">
                                        <div>
                                            <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider">STATUS</Text>
                                            <Text className="text-neutral-900 font-mono font-medium uppercase">
                                                {user.lineage_walkthrough_completed ? 'COMPLETED' : 'NOT COMPLETED'}
                                            </Text>
                                        </div>
                                        <div>
                                            <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider">COMPLETION COUNT</Text>
                                            <Text className="text-neutral-900 font-mono font-medium">
                                                {user.lineage_walkthrough_count || 0}
                                            </Text>
                                        </div>
                                        {user.lineage_walkthrough_completed_at && (
                                            <div>
                                                <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider">LAST COMPLETED</Text>
                                                <Text className="text-neutral-900 font-mono font-medium text-xs">
                                                    {formatDate(user.lineage_walkthrough_completed_at)}
                                                </Text>
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        onClick={handleResetLineageWalkthrough}
                                        disabled={isUpdating}
                                        className="w-full px-4 py-2 font-mono text-sm uppercase tracking-wider transition-colors bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0"
                                    >
                                        {isUpdating ? 'RESETTING...' : 'RESET'}
                                    </button>
                                </div>

                                {/* Live Session Walkthrough */}
                                <div className="bg-neutral-0 hairline p-4 space-y-3">
                                    <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider">LIVE SESSION WALKTHROUGH</Text>
                                    <div className="space-y-2">
                                        <div>
                                            <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider">STATUS</Text>
                                            <Text className="text-neutral-900 font-mono font-medium uppercase">
                                                {user.live_session_walkthrough_completed ? 'COMPLETED' : 'NOT COMPLETED'}
                                            </Text>
                                        </div>
                                        <div>
                                            <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider">COMPLETION COUNT</Text>
                                            <Text className="text-neutral-900 font-mono font-medium">
                                                {user.live_session_walkthrough_count || 0}
                                            </Text>
                                        </div>
                                        {user.live_session_walkthrough_completed_at && (
                                            <div>
                                                <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider">LAST COMPLETED</Text>
                                                <Text className="text-neutral-900 font-mono font-medium text-xs">
                                                    {formatDate(user.live_session_walkthrough_completed_at)}
                                                </Text>
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        onClick={handleResetLiveSessionWalkthrough}
                                        disabled={isUpdating}
                                        className="w-full px-4 py-2 font-mono text-sm uppercase tracking-wider transition-colors bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0"
                                    >
                                        {isUpdating ? 'RESETTING...' : 'RESET'}
                                    </button>
                                </div>
                            </div>
                        </Section>

                        {/* Quick Stats */}
                        <Section>
                            <Heading level={4} className="text-neutral-900 font-mono uppercase tracking-wider mb-4">QUICK STATS</Heading>
                            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                                <div className="bg-neutral-0 hairline p-4 text-center">
                                    <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider mb-2">RECIPES</Text>
                                    <Text className="text-neutral-900 text-2xl font-bold font-mono">{analytics.analytics.recipes.total_recipes || 0}</Text>
                                    <Text className="text-neutral-900 opacity-60 text-xs font-mono">
                                        {analytics.analytics.recipes.original_recipes || 0} original
                                    </Text>
                                    {user.engagement_tier && (
                                        <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider mt-1">
                                            {user.engagement_tier} tier
                                        </Text>
                                    )}
                                </div>
                                <div className="bg-neutral-0 hairline p-4 text-center">
                                    <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider mb-2">FORKED</Text>
                                    <Text className="text-neutral-900 text-2xl font-bold font-mono">{analytics.analytics.recipes.forked_recipes || 0}</Text>
                                </div>
                                <div className="bg-neutral-0 hairline p-4 text-center">
                                    <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider mb-2">BASES</Text>
                                    <Text className="text-neutral-900 text-2xl font-bold font-mono">{analytics.analytics.bases.total_bases || 0}</Text>
                                    <Text className="text-neutral-900 opacity-60 text-xs font-mono">
                                        {analytics.analytics.bases.public_bases || 0} public
                                    </Text>
                                </div>
                                <div className="bg-neutral-0 hairline p-4 text-center">
                                    <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider mb-2">SESSIONS</Text>
                                    <Text className="text-neutral-900 text-2xl font-bold font-mono">{analytics.analytics.sessions.total_sessions || 0}</Text>
                                    <Text className="text-neutral-900 opacity-60 text-xs font-mono">
                                        {analytics.analytics.sessions.completed_sessions || 0} completed
                                    </Text>
                                </div>
                                <div className="bg-neutral-0 hairline p-4 text-center">
                                    <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider mb-2">AVG TIME</Text>
                                    <Text className="text-neutral-900 text-lg font-bold font-mono">{formatDuration(analytics.analytics.sessions.avg_session_duration)}</Text>
                                </div>
                                <div className="bg-neutral-0 hairline p-4 text-center">
                                    <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider mb-2">ACTUAL AVG</Text>
                                    <Text className="text-neutral-900 text-lg font-bold font-mono">{formatSeconds(analytics.analytics.sessions.avg_actual_duration)}</Text>
                                </div>
                            </div>
                        </Section>
                    </>
                )}
            </div>
        </Modal>
    );
};

export default UserDetailModal; 
